import React, { useEffect, useMemo } from 'react';

import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import {
  Circle,
  CircleMarker,
  Tooltip as LeafletTooltip,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
  useMap,
} from 'react-leaflet';

import { Box, Typography } from '@mui/material';

// Fix default marker icon: Leaflet's default icon paths are resolved via CSS url()
// which breaks under webpack/rspack bundlers that rewrite asset paths. We import
// the PNGs directly so the bundler resolves correct URLs at build time.
const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const MIN_RADIUS_METERS = 2000;

/**
 * Compute the distance in meters between the attraction center and the
 * farthest offer point. Returns the minimum radius if no offers exist.
 */
function computeDynamicRadius(
  center: [number, number],
  offerPoints: Array<{ latitude: number; longitude: number }>,
  minRadius: number,
): number {
  const centerLatLng = L.latLng(center);
  let maxDist = 0;
  for (const point of offerPoints) {
    const dist = centerLatLng.distanceTo(L.latLng(point.latitude, point.longitude));
    if (dist > maxDist) {
      maxDist = dist;
    }
  }
  // Add 30% padding so the farthest offer is comfortably inside the circle
  const padded = maxDist * 1.3;
  return Math.max(padded, minRadius);
}

/**
 * Fits the map viewport to include the attraction marker, all offer points,
 * and the proximity radius circle. Adjusts zoom dynamically so every offer
 * is visible regardless of distance from the attraction.
 */
function AutoFitBounds({
  attractionLatLng,
  offerPoints,
  radiusMeters,
}: {
  attractionLatLng: L.LatLngExpression;
  offerPoints: Array<{ latitude: number; longitude: number }>;
  radiusMeters: number;
}) {
  const map = useMap();

  useEffect(() => {
    const bounds = L.latLngBounds([attractionLatLng]);

    // Include the proximity radius circle edge
    const center = L.latLng(attractionLatLng as [number, number]);
    const radiusBounds = center.toBounds(radiusMeters * 2);
    bounds.extend(radiusBounds);

    // Include every offer point
    for (const point of offerPoints) {
      bounds.extend([point.latitude, point.longitude]);
    }

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [map, attractionLatLng, offerPoints, radiusMeters]);

  return null;
}

/**
 * Offset co-located offers so stacked pins are individually visible.
 * Groups by rounded lat/lng (~11m precision) and fans duplicates in a
 * small circle around the shared location.
 */
function applyStackOffset(
  points: Array<{ latitude: number; longitude: number }>,
): Array<{ latitude: number; longitude: number }> {
  const PRECISION = 4; // ~11m grouping
  const OFFSET = 0.003; // ~330m spread radius, visible at zoom 12-14
  const groups = new Map<string, number[]>();

  for (let i = 0; i < points.length; i++) {
    const key = `${points[i].latitude.toFixed(PRECISION)},${points[i].longitude.toFixed(PRECISION)}`;
    const list = groups.get(key) ?? [];
    list.push(i);
    groups.set(key, list);
  }

  const result = points.map((p) => ({ ...p }));
  for (const indices of groups.values()) {
    if (indices.length < 2) continue;
    const angleStep = (2 * Math.PI) / indices.length;
    for (let j = 0; j < indices.length; j++) {
      const idx = indices[j];
      result[idx] = {
        ...result[idx],
        latitude: result[idx].latitude + OFFSET * Math.cos(angleStep * j),
        longitude: result[idx].longitude + OFFSET * Math.sin(angleStep * j),
      };
    }
  }
  return result;
}

function formatRadius(meters: number): string {
  if (meters >= 1000) {
    const km = meters / 1000;
    return km % 1 === 0 ? `${km} km` : `${km.toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
}

interface OfferPoint {
  id: string;
  title: string;
  latitude: number | null;
  longitude: number | null;
  relevanceScore: number;
  customerVisible?: boolean;
}

interface ProximityMapProps {
  latitude: number;
  longitude: number;
  name: string;
  radiusMeters?: number;
  offers?: OfferPoint[];
  totalOffers?: number;
  highlightOfferId?: string | null;
  onOfferClick?: (offerId: string) => void;
}

export default function ProximityMap({
  latitude,
  longitude,
  name,
  radiusMeters,
  offers = [],
  totalOffers,
  highlightOfferId,
  onOfferClick,
}: ProximityMapProps) {
  if (latitude == null || longitude == null) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="caption" color="text.disabled">
          No coordinates
        </Typography>
      </Box>
    );
  }

  // Filter offers with valid coordinates
  const offerPoints = offers.filter(
    (o): o is OfferPoint & { latitude: number; longitude: number } => o.latitude != null && o.longitude != null,
  );

  // Offset co-located offers so stacked pins become individually visible.
  const spreadPoints = useMemo(() => applyStackOffset(offerPoints), [offerPoints]);

  // Dynamic radius: cover all offers with a 30% padding, minimum 2km.
  // An explicit radiusMeters prop overrides the dynamic calculation.
  const effectiveRadius = useMemo(
    () => radiusMeters ?? computeDynamicRadius([latitude, longitude], offerPoints, MIN_RADIUS_METERS),
    [radiusMeters, latitude, longitude, offerPoints],
  );

  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={13}
      scrollWheelZoom={false}
      zoomControl={false}
      style={{ width: '100%', height: '100%' }}
    >
      <AutoFitBounds
        attractionLatLng={[latitude, longitude]}
        offerPoints={offerPoints}
        radiusMeters={effectiveRadius}
      />
      <ZoomControl position="bottomright" />
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />

      {/* Dynamic proximity radius with label */}
      <Circle
        center={[latitude, longitude]}
        radius={effectiveRadius}
        pathOptions={{
          color: '#1A56DB',
          fillColor: '#1A56DB',
          fillOpacity: 0.06,
          weight: 1.5,
          dashArray: '6 4',
        }}
      >
        <LeafletTooltip direction="bottom" permanent className="radius-label">
          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#1A56DB' }}>
            {formatRadius(effectiveRadius)} radius
          </span>
        </LeafletTooltip>
      </Circle>

      {/* Offer location points: green = customer visible, orange = candidate */}
      {offerPoints.map((offer, idx) => {
        const spread = spreadPoints[idx];
        const isVisible = offer.customerVisible !== false;
        const isHighlighted = highlightOfferId === offer.id;
        const markerColor = isHighlighted ? '#DC2626' : isVisible ? '#059669' : '#D97706';
        const visibilityLabel = isVisible ? 'Visible to customer' : 'Curator candidate';
        return (
          <CircleMarker
            key={offer.id}
            center={[spread.latitude, spread.longitude]}
            radius={isHighlighted ? 14 : 8}
            pathOptions={{
              color: isHighlighted ? '#DC2626' : '#fff',
              fillColor: markerColor,
              fillOpacity: isHighlighted ? 1 : 0.85,
              weight: isHighlighted ? 3 : 1.5,
            }}
            eventHandlers={{
              click: () => onOfferClick?.(offer.id),
            }}
          >
            <LeafletTooltip direction="top" offset={[0, -8]} sticky>
              <span style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {offer.title.length > 50 ? `${offer.title.substring(0, 50)}...` : offer.title}
              </span>
              <br />
              <span style={{ fontSize: '0.6rem', color: markerColor, fontWeight: 600 }}>{visibilityLabel}</span>
              <br />
              <Typography variant="caption" color="text.secondary" component="span" sx={{ fontSize: '0.6rem' }}>
                {Math.round(offer.relevanceScore * 100)}% relevance
              </Typography>
            </LeafletTooltip>
          </CircleMarker>
        );
      })}

      {/* Attraction marker (on top) */}
      <Marker position={[latitude, longitude]} icon={defaultIcon}>
        <Popup>
          <strong>{name}</strong>
          <br />
          <Typography variant="caption" color="text.secondary" component="span" sx={{ fontSize: '0.75rem' }}>
            {offerPoints.length} offers within {formatRadius(effectiveRadius)}
          </Typography>
        </Popup>
      </Marker>
    </MapContainer>
  );
}
